import React, { useState, useMemo, useEffect } from 'react'
import ProtectedRoute from '../../components/ProtectedRoute'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/stores/page-title-store'
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Users,
  Clock,
  Coffee,
  Plane,
  Baby,
  Heart,
  Target,
  Filter,
  Eye,
  AlertCircle,
  Plus
} from 'lucide-react'
import { getMyDirectReports } from '@/api/workforce'
import type { Employee } from '@/types/workforce'
import { useCalendarEvents } from '@/hooks/useCalendarEvents'
import EventCreationModal from './components/EventCreationModal'
import { CalendarSkeleton } from '@/components/common/CalendarSkeleton'
import WeekView from './components/WeekView'
import { format, startOfMonth, endOfMonth } from 'date-fns'
import type { CalendarEvent, EventTypeFilter, CalendarDay } from '@/types/calendar'

// Event type configurations
const EVENT_TYPE_CONFIG = {
  'vacation': {
    label: 'Férias',
    icon: Plane,
    color: '#D2FA52',
    bgColor: 'bg-brand-primary',
    textColor: 'text-brand-dark',
    borderColor: 'border-brand-primary'
  },
  'time-off': {
    label: 'Folga',
    icon: Coffee,
    color: '#4760E0',
    bgColor: 'bg-brand-blue',
    textColor: 'text-white',
    borderColor: 'border-brand-blue'
  },
  'licenca-paternidade': {
    label: 'Licença Paternidade',
    icon: Heart,
    color: '#3B82F6',
    bgColor: 'bg-info',
    textColor: 'text-white',
    borderColor: 'border-info'
  },
  'licenca-maternidade': {
    label: 'Licença Maternidade',
    icon: Baby,
    color: '#F59E0B',
    bgColor: 'bg-warning',
    textColor: 'text-white',
    borderColor: 'border-warning'
  },
  '1-1': {
    label: '1:1 Meeting',
    icon: Users,
    color: '#10B981',
    bgColor: 'bg-success',
    textColor: 'text-white',
    borderColor: 'border-success'
  },
  'pdi': {
    label: 'PDI',
    icon: Target,
    color: '#D90A2C',
    bgColor: 'bg-brand-red',
    textColor: 'text-white',
    borderColor: 'border-brand-red'
  }
}

// Calendar component
function CalendarContent() {
  const { user } = useAuth()
  usePageTitle('Calendário')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month')
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [eventTypeFilter, setEventTypeFilter] = useState<EventTypeFilter[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [subordinatesData, setSubordinatesData] = useState<Employee[]>([])
  const [loadingSubordinates, setLoadingSubordinates] = useState(true)

  // Fetch calendar events from API
  const startDate = format(startOfMonth(currentDate), 'yyyy-MM-dd')
  const endDate = format(endOfMonth(currentDate), 'yyyy-MM-dd')

  const { data: events = [], isLoading, error, refetch } = useCalendarEvents(
    startDate,
    endDate,
    eventTypeFilter.length > 0 ? eventTypeFilter : undefined
  )

  // Load subordinates for event creation
  useEffect(() => {
    const loadSubordinates = async () => {
      if (!user?.employee?.email) {
        setLoadingSubordinates(false)
        return
      }

      try {
        setLoadingSubordinates(true)
        const result = await getMyDirectReports({ limit: 100 })
        setSubordinatesData(result.items)
      } catch (err) {
        console.error('Error loading subordinates:', err)
      } finally {
        setLoadingSubordinates(false)
      }
    }

    loadSubordinates()
  }, [user])

  // Filter events by selected types
  const filteredEvents = useMemo(() => {
    if (!events || !Array.isArray(events)) return []
    if (eventTypeFilter.length === 0) return events
    return events.filter(event => eventTypeFilter.includes(event.type as EventTypeFilter))
  }, [events, eventTypeFilter])

  // Calculate week range for stats filtering
  const weekRange = useMemo(() => {
    if (viewMode !== 'week') return null

    const current = new Date(currentDate)
    const dayOfWeek = current.getDay()

    const start = new Date(current)
    start.setDate(current.getDate() - dayOfWeek)
    start.setHours(0, 0, 0, 0)

    const end = new Date(start)
    end.setDate(start.getDate() + 6)
    end.setHours(23, 59, 59, 999)

    return { start, end }
  }, [currentDate, viewMode])

  // Filter events by view mode (week or month)
  const viewFilteredEvents = useMemo(() => {
    if (!filteredEvents || !Array.isArray(filteredEvents)) return []
    if (viewMode === 'week' && weekRange) {
      return filteredEvents.filter(event => {
        const eventStart = new Date(event.startDate)
        const eventEnd = new Date(event.endDate)
        return eventStart <= weekRange.end && eventEnd >= weekRange.start
      })
    }
    return filteredEvents
  }, [filteredEvents, viewMode, weekRange])

  // Stats for the header (filtered by view mode)
  const stats = useMemo(() => ({
    totalEvents: viewFilteredEvents.length,
    upcomingEvents: viewFilteredEvents.filter(e => new Date(e.startDate) > new Date()).length,
    activeLeaves: viewFilteredEvents.filter(e =>
      ['vacation', 'licenca-maternidade', 'licenca-paternidade'].includes(e.type) &&
      new Date(e.startDate) <= new Date() &&
      new Date(e.endDate) >= new Date()
    ).length,
    scheduledMeetings: viewFilteredEvents.filter(e => ['1-1', 'pdi'].includes(e.type)).length
  }), [viewFilteredEvents])

  // Show loading state
  if (isLoading || loadingSubordinates) {
    return <CalendarSkeleton />
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-brand-red/20 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="h-8 w-8 text-brand-red" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white mb-2">Erro ao Carregar Calendário</h2>
            <p className="text-brand-gray-light">{error.message}</p>
            <button
              onClick={() => refetch()}
              className="btn-primary mt-4"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Calendar navigation
  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate)
    if (direction === 'prev') {
      newDate.setMonth(currentDate.getMonth() - 1)
    } else {
      newDate.setMonth(currentDate.getMonth() + 1)
    }
    setCurrentDate(newDate)
  }

  const toggleEventTypeFilter = (eventType: EventTypeFilter) => {
    setEventTypeFilter(prev =>
      prev.includes(eventType)
        ? prev.filter(t => t !== eventType)
        : [...prev, eventType]
    )
  }

  // Generate calendar days for month view
  const generateCalendarDays = (): CalendarDay[] => {
    const start = startOfMonth(currentDate)
    const end = endOfMonth(currentDate)
    const startDate = new Date(start)
    startDate.setDate(startDate.getDate() - start.getDay()) // Start from Sunday

    const days: CalendarDay[] = []
    const iterationDate = new Date(startDate)

    // Generate 42 days (6 weeks)
    for (let i = 0; i < 42; i++) {
      const dayEvents = filteredEvents.filter(event => {
        const eventStart = new Date(event.startDate)
        const eventEnd = new Date(event.endDate)
        const currentDay = new Date(iterationDate)
        currentDay.setHours(0, 0, 0, 0)
        const nextDay = new Date(currentDay)
        nextDay.setDate(nextDay.getDate() + 1)

        return eventStart < nextDay && eventEnd >= currentDay
      })

      days.push({
        date: new Date(iterationDate),
        events: dayEvents,
        isCurrentMonth: iterationDate.getMonth() === currentDate.getMonth(),
        isToday: iterationDate.toDateString() === new Date().toDateString()
      })

      iterationDate.setDate(iterationDate.getDate() + 1)
    }

    return days
  }

  const calendarDays = generateCalendarDays()

  return (
    <div className="space-y-8">
        {/* Header */}
        <div className="flex justify-end">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center justify-center gap-2 group"
            >
              <Plus size={16} className="transition-transform duration-200 group-hover:rotate-90" />
              <span>Novo Evento</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="dashboard-grid lg:grid-cols-4">
          <div className="metric-card">
            <div className="flex items-center justify-between mb-2">
              <CalendarIcon className="text-brand-primary" size={20} />
            </div>
            <div className="metric-value text-brand-primary">{stats.totalEvents}</div>
            <div className="metric-label">Total de Eventos</div>
          </div>
          <div className="metric-card">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-brand-blue" size={20} />
            </div>
            <div className="metric-value text-brand-blue">{stats.upcomingEvents}</div>
            <div className="metric-label">Eventos Futuros</div>
          </div>
          <div className="metric-card">
            <div className="flex items-center justify-between mb-2">
              <Plane className="text-warning" size={20} />
            </div>
            <div className="metric-value text-warning">{stats.activeLeaves}</div>
            <div className="metric-label">Ausências Ativas</div>
          </div>
          <div className="metric-card">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-success" size={20} />
            </div>
            <div className="metric-value text-success">{stats.scheduledMeetings}</div>
            <div className="metric-label">Reuniões Agendadas</div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-brand-dark-light rounded-monumenta p-4 border border-brand-gray/10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center gap-3">
              <Filter size={16} className="text-brand-gray" />
              <span className="font-display font-medium text-white">Filtros:</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(EVENT_TYPE_CONFIG).map(([type, config]) => {
                  const Icon = config.icon
                  const isActive = eventTypeFilter.includes(type as EventTypeFilter)
                  return (
                    <button
                      key={type}
                      onClick={() => toggleEventTypeFilter(type as EventTypeFilter)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-monumenta text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? `${config.bgColor} ${config.textColor}`
                          : 'bg-brand-dark-lighter text-brand-gray hover:text-white'
                      }`}
                    >
                      <Icon size={14} />
                      {config.label}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="card-hover">
          {/* Calendar Header with View Toggle */}
          <div className="p-6 border-b border-brand-gray/20">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              {/* View Mode Toggle */}
              <div className="flex items-center gap-2 bg-brand-dark-lighter p-1 rounded-monumenta">
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-4 py-2 rounded-monumenta text-sm font-medium transition-all duration-200 ${
                    viewMode === 'month'
                      ? 'bg-brand-primary text-brand-dark'
                      : 'text-brand-gray hover:text-white'
                  }`}
                >
                  Mês
                </button>
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-4 py-2 rounded-monumenta text-sm font-medium transition-all duration-200 ${
                    viewMode === 'week'
                      ? 'bg-brand-primary text-brand-dark'
                      : 'text-brand-gray hover:text-white'
                  }`}
                >
                  Semana
                </button>
              </div>

              {/* Navigation Controls - Only show for month view */}
              {viewMode === 'month' && (
                <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-4">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => navigateMonth('prev')}
                      className="p-2 rounded-monumenta bg-brand-dark-lighter hover:bg-brand-primary hover:text-brand-dark transition-all duration-200"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <h2 className="font-display text-xl font-semibold text-white">
                      {currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                    </h2>
                    <button
                      onClick={() => navigateMonth('next')}
                      className="p-2 rounded-monumenta bg-brand-dark-lighter hover:bg-brand-primary hover:text-brand-dark transition-all duration-200"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                  <button
                    onClick={() => setCurrentDate(new Date())}
                    className="btn-ghost text-sm px-3 py-1.5"
                  >
                    Hoje
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Conditional View Rendering */}
          {viewMode === 'month' ? (
            /* Month View */
            <div className="p-6">
              {/* Days of week header */}
              <div className="grid grid-cols-7 gap-1 mb-4">
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-brand-gray">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => (
                  <div
                    key={index}
                    className={`min-h-[120px] p-2 border border-brand-gray/10 rounded transition-all duration-200 ${
                      day.isCurrentMonth
                        ? 'bg-brand-dark-light hover:bg-brand-dark-lighter'
                        : 'bg-brand-dark-light/50 text-brand-gray/50'
                    } ${day.isToday ? 'border-brand-primary bg-brand-primary/5' : ''}`}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      day.isToday
                        ? 'text-brand-primary font-bold'
                        : day.isCurrentMonth
                          ? 'text-white'
                          : 'text-brand-gray/50'
                    }`}>
                      {day.date.getDate()}
                    </div>

                    {/* Events for this day */}
                    <div className="space-y-1">
                      {day.events.slice(0, 3).map((event) => {
                        const config = EVENT_TYPE_CONFIG[event.type]
                        if (!config) return null
                        const Icon = config.icon

                        return (
                          <div
                            key={event.eventId}
                            onClick={() => setSelectedEvent(event)}
                            className={`text-xs p-1.5 rounded cursor-pointer transition-all duration-200 ${config.bgColor} ${config.textColor} hover:scale-105`}
                            title={`${event.title} - ${event.description || ''}`}
                          >
                            <div className="flex items-center gap-1 truncate">
                              <Icon size={10} />
                              <span className="truncate">{event.employeeName}</span>
                            </div>
                          </div>
                        )
                      })}
                      {day.events.length > 3 && (
                        <div className="text-xs text-brand-gray bg-brand-dark-lighter/50 p-1 rounded text-center">
                          +{day.events.length - 3} mais
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Week View */
            <WeekView
              events={filteredEvents}
              currentDate={currentDate}
              onEventClick={setSelectedEvent}
              onDateChange={setCurrentDate}
              eventTypeConfig={EVENT_TYPE_CONFIG}
            />
          )}
        </div>

        {/* Event Detail Modal */}
        {selectedEvent && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-brand-dark-light rounded-monumenta max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-brand-gray/20 shadow-monumenta-xl animate-scale-in">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const config = EVENT_TYPE_CONFIG[selectedEvent.type]
                      if (!config) return null
                      const Icon = config.icon
                      return (
                        <div className={`p-3 rounded-monumenta ${config.bgColor}`}>
                          <Icon size={24} className={config.textColor} />
                        </div>
                      )
                    })()}
                    <div>
                      <h2 className="font-display text-xl font-bold text-white">
                        {selectedEvent.title}
                      </h2>
                      <p className="text-brand-gray">
                        {EVENT_TYPE_CONFIG[selectedEvent.type]?.label || selectedEvent.type}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="p-2 rounded-monumenta bg-brand-dark hover:bg-brand-dark-lighter text-brand-gray hover:text-white transition-all duration-200"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Event Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-display font-medium text-white mb-2">Colaborador</h4>
                      <p className="text-brand-gray">{selectedEvent.employeeName}</p>
                      <p className="text-sm text-brand-gray opacity-75">{selectedEvent.department}</p>
                    </div>
                    <div>
                      <h4 className="font-display font-medium text-white mb-2">Status</h4>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        selectedEvent.status === 'confirmed'
                          ? 'bg-success/20 text-success border border-success/20'
                          : selectedEvent.status === 'pending'
                          ? 'bg-warning/20 text-warning border border-warning/20'
                          : 'bg-brand-red/20 text-brand-red border border-brand-red/20'
                      }`}>
                        {selectedEvent.status === 'confirmed' ? 'Confirmado' :
                         selectedEvent.status === 'pending' ? 'Pendente' : 'Cancelado'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-display font-medium text-white mb-2">Período</h4>
                    <p className="text-brand-gray">
                      {selectedEvent.allDay ? (
                        <>
                          {new Date(selectedEvent.startDate).toLocaleDateString('pt-BR')}
                          {selectedEvent.startDate !== selectedEvent.endDate && (
                            <> até {new Date(selectedEvent.endDate).toLocaleDateString('pt-BR')}</>
                          )}
                        </>
                      ) : (
                        <>
                          {new Date(selectedEvent.startDate).toLocaleDateString('pt-BR')} às{' '}
                          {new Date(selectedEvent.startDate).toLocaleTimeString('pt-BR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })} -{' '}
                          {new Date(selectedEvent.endDate).toLocaleTimeString('pt-BR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </>
                      )}
                    </p>
                  </div>

                  {selectedEvent.description && (
                    <div>
                      <h4 className="font-display font-medium text-white mb-2">Descrição</h4>
                      <p className="text-brand-gray bg-brand-dark/50 p-3 rounded-monumenta border border-brand-gray/10">
                        {selectedEvent.description}
                      </p>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-3 pt-4 border-t border-brand-gray/20">
                    <button className="btn-primary flex items-center gap-2">
                      <Eye size={16} />
                      Ver Detalhes
                    </button>
                    <button className="btn-secondary">
                      Editar
                    </button>
                    <button
                      onClick={() => setSelectedEvent(null)}
                      className="btn-ghost"
                    >
                      Fechar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Event Creation Modal */}
        <EventCreationModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            // Events will auto-refresh via React Query
          }}
          subordinates={subordinatesData}
        />
      </div>
  )
}

export default function CalendarPage() {
  return (
    <ProtectedRoute>
      <CalendarContent />
    </ProtectedRoute>
  )
}
